import {isPlatformServer} from '@angular/common';
import {inject, PLATFORM_ID, RESPONSE_INIT} from '@angular/core';
import {Router, type ResolveFn} from '@angular/router';
import {redirectTo404} from '@app/navigation/page-not-found/page-not-found.component';
import {GetDatasetsInProjectParams, type ProjectResponse} from '@backend';
import {TileInfo} from '@models/tile-info';
import {mapSearchProjectToBaseModel, PrivateProjectPageData, ProjectBaseModel} from '@project/project-base.model';
import {LandingServiceBrokerService} from '@services/landing-service-broker/landing-service-broker.service';
import {SearchService} from '@services/search/search.service';
import {generateDatasetTilesFromResponse, generateTileList} from '@utilities/tiles';
import {lastValueFrom} from 'rxjs';

export const privateProjectResolver: ResolveFn<PrivateProjectPageData | undefined> = async (route, _state) => {
  if (isPlatformServer(inject(PLATFORM_ID))) return undefined;

  const searchService = inject(SearchService);
  const landingServiceBroker = inject(LandingServiceBrokerService);
  const router = inject(Router);
  const response = inject(RESPONSE_INIT, {optional: true});

  const projectID = route.paramMap.get('projectId');
  if (!projectID) return redirectTo404(router, response);

  try {
    // Load public version of project
    const {results} = await searchService.searchGeneric(
      {id: {terms: projectID}},
      {public_view: false},
      'Projects',
      1,
      1,
    );

    const publicProject = results[0];
    if (!publicProject) return redirectTo404(router, response);

    const orgName = publicProject.storage_organization?.name;
    if (!orgName) return redirectTo404(router, response);

    const serviceInstanceMap = await landingServiceBroker.getByOrganizationName(orgName);
    if (!serviceInstanceMap) return redirectTo404(router, response);

    const privateProject: ProjectResponse = await lastValueFrom(
      serviceInstanceMap.ProjectsService.getProject(projectID),
    );

    // 404 if the project is scheduled for deletion:
    if (privateProject.scheduled_for_deletion_on) {
      return redirectTo404(router, response);
    }

    const baseProject: ProjectBaseModel = mapSearchProjectToBaseModel(publicProject);

    // Dataset Tiles
    // Manually request the first 100 datasets from the landing service:
    const projectDatasetsPayload: GetDatasetsInProjectParams = {
      page: 1,
      per_page: 250,
    };
    const projectDatasets = await lastValueFrom(
      serviceInstanceMap.ProjectsService.getDatasetsInProject(projectID, projectDatasetsPayload),
    );

    const datasetTiles = generateDatasetTilesFromResponse(projectDatasets.datasets);

    // Resource Tiles
    const resourceIDs = privateProject.associated_resource_ids ?? [];
    let resourceTiles: TileInfo[] = [];

    if (resourceIDs.length > 0) {
      const {results} = await searchService.searchGeneric(
        {id: {terms: resourceIDs}},
        {public_view: false},
        'Resources',
        1,
        resourceIDs.length,
      );

      const resourceIdSet = new Set(resourceIDs);
      resourceTiles = generateTileList(results.filter(r => r.id && resourceIdSet.has(r.id)));
    }

    return {
      project: baseProject,
      privateProject,
      projectsService: serviceInstanceMap.ProjectsService,
      datasetTiles,
      resourceTiles,
    };
  } catch (err) {
    console.error('Error in privateProjectResolver:', err);
    return redirectTo404(router, response);
  }
};
